import React, { useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../../commonDesign/Button'
import Icon from '../../../../../../../atoms/Icon'

const TARGET_LABELS = {
  purpose: 'Purpose',
  'presentation-option': 'Presentation',
  definition: 'Full definition'
}

const OP_LABELS = {
  upsert: 'Upsert',
  remove: 'Remove'
}

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-ai-200)',
    background: 'linear-gradient(135deg, var(--color-ai-050) 0%, white 100%)',
    borderRadius: 'var(--border-radius-lg)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    margin: '12px 0',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  applied: {
    border: '1px solid var(--color-success-300)',
    background: 'var(--color-success-050)'
  },
  dismissed: {
    border: '1px dashed var(--color-neutral-300)',
    background: 'var(--color-neutral-050)'
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  badge: {
    fontSize: '9.5px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '2px 7px',
    borderRadius: '3px',
    fontFamily: "'SF Mono', 'Fira Code', Menlo, Consolas, monospace"
  },
  targetBadge: {
    backgroundColor: 'var(--color-ai-100)',
    color: 'var(--color-ai-700)'
  },
  opUpsert: {
    backgroundColor: 'var(--color-info-100, #dbeafe)',
    color: 'var(--color-info-700, #1d4ed8)'
  },
  opRemove: {
    backgroundColor: 'var(--color-danger-100, #fee2e2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  code: {
    fontFamily: "'SF Mono', 'Fira Code', Menlo, Consolas, monospace",
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-neutral-800)'
  },
  rationale: {
    fontSize: '12.5px',
    color: 'var(--color-neutral-700)',
    lineHeight: 1.55,
    margin: 0
  },
  testPlan: {
    fontSize: '11.5px',
    color: 'var(--color-neutral-600)',
    background: 'rgba(255, 255, 255, 0.55)',
    padding: '8px 10px',
    borderLeft: '3px solid var(--color-ai-300)',
    borderRadius: '0 4px 4px 0',
    lineHeight: 1.5
  },
  testPlanLabel: {
    fontSize: '9.5px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--color-ai-600)',
    marginBottom: '2px'
  },
  jsonToggle: {
    fontSize: '10.5px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    background: 'transparent',
    border: 'none',
    padding: '2px 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    '&:hover': {
      color: 'var(--color-neutral-700)'
    }
  },
  jsonBlock: {
    fontFamily: "'SF Mono', 'Fira Code', Menlo, Consolas, monospace",
    fontSize: '11px',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    border: '1px solid var(--color-ai-200)',
    borderRadius: '4px',
    padding: '8px 10px',
    overflow: 'auto',
    maxHeight: '300px',
    whiteSpace: 'pre',
    color: 'var(--color-neutral-800)',
    margin: 0
  },
  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  appliedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: 'var(--color-success-700)',
    fontWeight: 600,
    fontSize: '12px'
  },
  dismissedBanner: {
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic',
    fontSize: '12px'
  }
}))

function summarizeValue (target, op, value) {
  if (op === 'remove') return null
  if (!value || typeof value !== 'object') return null

  if (target === 'purpose') {
    const colCount = Array.isArray(value.columns) ? value.columns.length : 0
    const tables = Array.isArray(value.tables) && value.tables.length
      ? value.tables.map(t => t.schema ? `${t.schema}.${t.tableCode}` : t.tableCode).join(', ')
      : null
    const bits = []
    if (value.displayName) bits.push(value.displayName)
    if (tables) bits.push(`from ${tables}`)
    if (colCount) bits.push(`${colCount} column${colCount === 1 ? '' : 's'}`)
    if (value.latch) bits.push('with latch')
    return bits.join(' • ')
  }

  if (target === 'presentation-option') {
    const bits = []
    if (value.displayName) bits.push(value.displayName)
    if (value.type) bits.push(value.type)
    if (value.config?.purposeCode) bits.push(`purpose: ${value.config.purposeCode}`)
    return bits.join(' • ')
  }

  if (target === 'definition') {
    const purposes = Object.keys(value.data?.purposes || {}).length
    const options = Object.keys(value.presentation?.options || {}).length
    return `${purposes} purpose${purposes === 1 ? '' : 's'} • ${options} option${options === 1 ? '' : 's'}`
  }

  return null
}

function ProposedBlueprintCard ({ proposalId, change, rationale, testPlan, onApply, autoAccept, isAlreadyApplied }) {
  const classes = useStyles()
  // Wait for `isAlreadyApplied` to flip before showing applied; onApply may open
  // a fork modal that the user can cancel.
  const [state, setState] = useState(isAlreadyApplied ? 'applied' : 'pending')
  const [autoApplyTriggered, setAutoApplyTriggered] = useState(false)
  const [showJson, setShowJson] = useState(false)
  const autoApplied = useRef(false)

  useEffect(() => {
    if (isAlreadyApplied && state !== 'applied' && state !== 'auto-applied') {
      setState(autoApplyTriggered ? 'auto-applied' : 'applied')
    }
  }, [isAlreadyApplied, state, autoApplyTriggered])

  useEffect(() => {
    if (autoAccept && state === 'pending' && onApply && !autoApplied.current && !isAlreadyApplied) {
      autoApplied.current = true
      setAutoApplyTriggered(true)
      onApply(change, proposalId)
    }
  }, [autoAccept, state, onApply, change, proposalId, isAlreadyApplied])

  const targetLabel = TARGET_LABELS[change?.target] || change?.target || 'Change'
  const opLabel = change?.op ? OP_LABELS[change.op] || change.op : null
  const summary = summarizeValue(change?.target, change?.op, change?.value)

  const valueJson = useMemo(() => {
    if (!change?.value) return ''
    try { return JSON.stringify(change.value, null, 2) } catch { return String(change.value) }
  }, [change?.value])

  if (state === 'dismissed') {
    return (
      <div className={`${classes.root} ${classes.dismissed}`}>
        <span className={classes.dismissedBanner}>{targetLabel} proposal dismissed</span>
      </div>
    )
  }

  const handleApply = () => {
    if (onApply) onApply(change, proposalId)
  }

  if (state === 'applied' || state === 'auto-applied') {
    return (
      <div className={`${classes.root} ${classes.applied}`}>
        <div className={classes.topRow}>
          <Icon name='check' customSize='16px' />
          <span className={classes.appliedBanner}>
            {state === 'auto-applied' ? `${targetLabel} auto-applied` : `${targetLabel} applied to draft`}
          </span>
          {change?.code && (
            <>
              <span className={classes.code}>{change.code}</span>
            </>
          )}
        </div>
        {rationale && <p className={classes.rationale}>{rationale}</p>}
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.topRow}>
        <span className={`${classes.badge} ${classes.targetBadge}`}>{targetLabel}</span>
        {opLabel && (
          <span className={`${classes.badge} ${change.op === 'remove' ? classes.opRemove : classes.opUpsert}`}>
            {opLabel}
          </span>
        )}
        {change?.code && <span className={classes.code}>{change.code}</span>}
        {summary && (
          <span style={{ fontSize: '11.5px', color: 'var(--color-neutral-500)' }}>
            — {summary}
          </span>
        )}
      </div>

      {rationale && <p className={classes.rationale}>{rationale}</p>}

      {testPlan && (
        <div className={classes.testPlan}>
          <div className={classes.testPlanLabel}>How to verify</div>
          {testPlan}
        </div>
      )}

      {valueJson && (
        <>
          <button
            className={classes.jsonToggle}
            onClick={() => setShowJson(p => !p)}
            type='button'
          >
            <Icon name={showJson ? 'chevronDown' : 'chevronRight'} customSize='12px' />
            {showJson ? 'Hide JSON' : 'Show JSON'}
          </button>
          {showJson && <pre className={classes.jsonBlock}>{valueJson}</pre>}
        </>
      )}

      <div className={classes.actions}>
        <SydButton size='sm' variant='ghost' onClick={() => setState('dismissed')}>
          Dismiss
        </SydButton>
        <SydButton size='sm' variant='primary' icon='check' onClick={handleApply}>
          Apply to draft
        </SydButton>
      </div>
    </div>
  )
}

ProposedBlueprintCard.propTypes = {
  proposalId: PropTypes.string,
  change: PropTypes.object.isRequired,
  rationale: PropTypes.string,
  testPlan: PropTypes.string,
  onApply: PropTypes.func,
  autoAccept: PropTypes.bool,
  isAlreadyApplied: PropTypes.bool
}

export default ProposedBlueprintCard
